import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import { Keys } from "@utils/enums";
import { noop } from "@utils/helpers";

import { Fade, FadeProps } from "../Fade/fade.component";
import { Portal } from "./portal.component";
import {
    conditionallyUpdateScrollbar,
    focusableElements,
    getOriginalBodyPadding,
    getTarget,
    setScrollbarWidth,
} from "./utils";

export type ModalProps = {
    isOpen: boolean;
    onClosed?: () => void;
    onOpened?: () => void;
    onEnter?: () => void;
    onExit?: () => void;
    toggle?: (e?: any) => void;
    unmountOnClose?: boolean;
    autoFocus: boolean;
    returnFocusAfterClose?: boolean;
    centered?: boolean;
    scrollable: boolean;
    size?: string;
    children: React.ReactNode;
    external?: React.ReactNode;
    backdrop: boolean | "static";
    container?: string | HTMLElement;
    role?: string;
    zIndex?: number | string;
    fade?: boolean;
    keyboard?: boolean;
    innerRef?: React.RefObject<any>;
    labelledBy?: string;
    className?: string;
    wrapClassName?: string;
    modalClassName?: string;
    backdropClassName?: string;
    contentClassName?: string;
    backdropTransition?: FadeProps<"div">;
    modalTransition?: FadeProps<"div">;
};

const FadeDefaultProps: FadeProps<"div"> = {
    tag: "div",
    baseClass: "fade",
    baseClassActive: "show",
    timeout: 150,
    appear: true,
    enter: true,
    exit: true,
    in: true,
};

const Modal = ({
    onClosed = noop,
    onOpened = noop,
    onEnter,
    onExit,
    children,
    external,
    keyboard = true,
    fade = true,
    isOpen: isOpenProps = false,
    unmountOnClose = true,
    toggle,
    autoFocus = true,
    returnFocusAfterClose = true,
    centered = false,
    scrollable = false,
    size,
    backdrop: backdropProp = true,
    container = "body",
    role = "dialog",
    zIndex = 1050,
    innerRef,
    labelledBy,
    className,
    wrapClassName,
    modalClassName,
    backdropClassName,
    contentClassName,
    modalTransition: modalTransitionProps = {
        timeout: 300,
    },
    backdropTransition: backdropTransitionProp = {
        mountOnEnter: true,
        timeout: 150,
    },
    ...attributes
}: ModalProps) => {
    const [isOpen, setIsOpen] = useState(isOpenProps);
    const [showStaticBackdropAnimation, setShowStaticBackdropAnimation] =
        useState(false);

    const elementRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const triggeringElementRef = useRef<Element | null>(null);
    const originalBodyPaddingRef = useRef<number | null>(null);
    const backdropAnimationTimeoutRef = useRef<NodeJS.Timeout | undefined>(
        undefined,
    );
    const mouseDownElementRef = useRef<HTMLElement | null>(null);
    const isMountedRef = useRef(false);

    useEffect(() => {
        if (onEnter) {
            onEnter();
        }

        isMountedRef.current = true;

        return () => {
            clearBackdropAnimationTimeout();

            if (onExit) {
                onExit();
            }

            destroy();
            close();
        };
    }, []);

    useEffect(() => {
        if (isOpenProps) {
            init();
            setIsOpen(true);

            if (autoFocus) {
                setFocus();
            }
        }
    }, [isOpenProps]);

    useEffect(() => {
        if (elementRef.current && zIndex) {
            elementRef.current.style.zIndex = zIndex.toString();
        }
    }, [zIndex]);

    const onInnerOpened = (node: any, isAppearing: boolean) => {
        onOpened();
        (modalTransitionProps.onEntered || noop)(node, isAppearing);
    };

    const onInnerClosed = (node: any) => {
        // so all methods get called before it is unmounted
        onClosed();
        (modalTransitionProps.onExited || noop)(node);

        if (unmountOnClose) {
            destroy();
        }
        close();

        if (isMountedRef.current) {
            setIsOpen(false);
        }
    };

    const setFocus = () => {
        if (
            dialogRef.current &&
            dialogRef.current.parentNode &&
            dialogRef.current.parentNode instanceof HTMLElement &&
            typeof dialogRef.current.parentNode.focus === "function"
        ) {
            dialogRef.current.parentNode.focus();
        }
    };

    const getFocusableChildren = () => {
        return (
            elementRef.current?.querySelectorAll<HTMLElement>(
                focusableElements.join(", "),
            ) || []
        );
    };

    const getFocusedChild = () => {
        let currentFocus;
        const focusableChildren = getFocusableChildren();

        try {
            currentFocus = document.activeElement;
        } catch (err) {
            currentFocus = focusableChildren[0];
        }
        return currentFocus;
    };

    // not mouseUp because scrollbar fires it, shouldn't close when user scrolls
    const handleBackdropClick = (e: any) => {
        if (e.target === mouseDownElementRef.current) {
            e.stopPropagation();

            const backdrop = dialogRef.current
                ? dialogRef.current.parentNode
                : null;

            if (
                backdrop &&
                e.target === backdrop &&
                backdropProp === "static"
            ) {
                handleStaticBackdropAnimation();
            }

            if (!isOpenProps || backdropProp !== true) return;

            if (backdrop && e.target === backdrop && toggle) {
                toggle(e);
            }
        }
    };

    const handleTab = (e: any) => {
        if (e.which !== 9) return;

        const focusableChildren = getFocusableChildren();
        const totalFocusable = focusableChildren.length;
        if (totalFocusable === 0) return;
        const currentFocus = getFocusedChild();

        let focusedIndex = 0;

        for (let i = 0; i < totalFocusable; i += 1) {
            if (focusableChildren[i] === currentFocus) {
                focusedIndex = i;
                break;
            }
        }

        if (e.shiftKey && focusedIndex === 0) {
            e.preventDefault();
            focusableChildren[totalFocusable - 1].focus();
        } else if (!e.shiftKey && focusedIndex === totalFocusable - 1) {
            e.preventDefault();
            focusableChildren[0].focus();
        }
    };

    const handleBackdropMouseDown = (e: any) => {
        mouseDownElementRef.current = e.target;
    };

    const handleEscape = (e: any) => {
        if (isOpenProps && e.keyCode === Keys.ESCAPE && toggle) {
            if (keyboard) {
                e.preventDefault();
                e.stopPropagation();

                toggle(e);
            } else if (backdropProp === "static") {
                e.preventDefault();
                e.stopPropagation();

                handleStaticBackdropAnimation();
            }
        }
    };

    const handleStaticBackdropAnimation = () => {
        clearBackdropAnimationTimeout();

        setShowStaticBackdropAnimation(true);
        backdropAnimationTimeoutRef.current = setTimeout(() => {
            setShowStaticBackdropAnimation(false);
        }, 100);
    };

    const init = () => {
        try {
            triggeringElementRef.current = document.activeElement;
        } catch (err) {
            triggeringElementRef.current = null;
        }

        if (!elementRef.current) {
            elementRef.current = document.createElement("div");
            elementRef.current.setAttribute("tabindex", "-1");
            elementRef.current.style.position = "relative";
            elementRef.current.style.zIndex = zIndex.toString();
            const mountContainer = getTarget(container);
            mountContainer.appendChild(elementRef.current);
        }

        originalBodyPaddingRef.current = getOriginalBodyPadding();
        conditionallyUpdateScrollbar();

        if (Modal.openCount === 0) {
            document.body.className = classNames(
                document.body.className,
                "modal-open",
            );
        }

        Modal.openCount += 1;
    };

    const destroy = () => {
        if (elementRef.current) {
            const mountContainer = getTarget(container);
            mountContainer.removeChild(elementRef.current);
            elementRef.current = null;
        }

        manageFocusAfterClose();
    };

    const manageFocusAfterClose = () => {
        if (
            triggeringElementRef.current &&
            triggeringElementRef.current instanceof HTMLElement
        ) {
            if (triggeringElementRef.current.focus && returnFocusAfterClose)
                triggeringElementRef.current.focus();
            triggeringElementRef.current = null;
        }
    };

    const close = () => {
        const modalOpenClassName = "modal-open";
        // Use regex to prevent matching `modal-open` as part of a different class, e.g. `my-modal-opened`
        const modalOpenClassNameRegex = new RegExp(
            `(^| )${modalOpenClassName}( |$)`,
        );
        document.body.className = document.body.className
            .replace(modalOpenClassNameRegex, " ")
            .trim();
        manageFocusAfterClose();
        Modal.openCount = Math.max(0, Modal.openCount - 1);

        if (originalBodyPaddingRef.current) {
            setScrollbarWidth(originalBodyPaddingRef.current);
        }
    };

    const renderModalDialog = () => {
        const dialogBaseClass = "modal-dialog";

        return (
            <div
                {...attributes}
                className={classNames(dialogBaseClass, className, {
                    [`modal-${size}`]: size,
                    [`${dialogBaseClass}-centered`]: centered,
                    [`${dialogBaseClass}-scrollable`]: scrollable,
                })}
                role="document"
                ref={c => {dialogRef.current = c}}
            >
                <div className={classNames("modal-content", contentClassName)}>
                    {children}
                </div>
            </div>
        );
    };

    const clearBackdropAnimationTimeout = () => {
        if (backdropAnimationTimeoutRef.current) {
            clearTimeout(backdropAnimationTimeoutRef.current);
            backdropAnimationTimeoutRef.current = undefined;
        }
    };

    if (!!elementRef.current && (isOpen || !unmountOnClose)) {
        const isModalHidden =
            !!elementRef.current && !isOpen && !unmountOnClose;
        elementRef.current.style.display = isModalHidden ? "none" : "block";

        const modalAttributes = {
            onClick: handleBackdropClick,
            onMouseDown: handleBackdropMouseDown,
            onKeyUp: handleEscape,
            onKeyDown: handleTab,
            style: { display: "block" },
            "aria-labelledby": labelledBy,
            role,
            tabIndex: "-1",
        };

        const hasTransition = fade;
        const modalTransition = {
            ...FadeDefaultProps,
            ...modalTransitionProps,
            baseClass: hasTransition ? modalTransitionProps.baseClass : "",
            timeout: hasTransition ? modalTransitionProps.timeout : 0,
        };
        const backdropTransition = {
            ...FadeDefaultProps,
            ...backdropTransitionProp,
            baseClass: hasTransition ? backdropTransitionProp.baseClass : "",
            timeout: hasTransition ? backdropTransitionProp.timeout : 0,
        };

        const Backdrop =
            backdropProp &&
            (hasTransition ? (
                <Fade
                    {...backdropTransition}
                    in={isOpenProps && !!backdropProp}
                    className={classNames("modal-backdrop", backdropClassName)}
                />
            ) : (
                <div
                    className={classNames(
                        "modal-backdrop",
                        "show",
                        backdropClassName,
                    )}
                />
            ));

        return (
            <Portal node={elementRef.current}>
                <div className={wrapClassName}>
                    <Fade
                        {...modalAttributes}
                        {...modalTransition}
                        in={isOpenProps}
                        onEntered={onInnerOpened}
                        onExited={onInnerClosed}
                        className={classNames(
                            "modal",
                            modalClassName,
                            showStaticBackdropAnimation && "modal-static",
                        )}
                        ref={innerRef}
                    >
                        {external}
                        {renderModalDialog()}
                    </Fade>
                    {Backdrop}
                </div>
            </Portal>
        );
    }
    return null;
};

Modal.openCount = 0;

export { Modal };
